// path: src/utils/socialAuthPlugins.js
// Access Cap native auth plugins without hard imports (web-safe).

import { getCapacitor } from "./nativePlatform";

/** Fresh in-app NativeAuth plugin (Apple + Facebook Limited Login). */
export function getNativeAuthPlugin() {
    const cap = getCapacitor();
    try {
        const plugin = cap && cap.Plugins && cap.Plugins.NativeAuth;
        return plugin || null;
    } catch (_) {
        return null;
    }
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Poll-based native auth wait. Cap promises that span Apple/Facebook sheets
 * often never resolve in WKWebView; short poll calls do.
 */
export async function pollUntilAuthResult({
    poll,
    onStatus,
    timeoutMs = 90000,
    intervalMs = 400,
}) {
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
        const result = await poll();
        const status = result && result.status;
        if (typeof onStatus === "function") {
            onStatus(status || "pending");
        }
        if (status === "completed" || status === "error" || status === "cancelled") {
            return result;
        }
        await sleep(intervalMs);
    }
    return {
        status: "error",
        error: "Timed out waiting for the system sign-in sheet.",
    };
}

/** Random raw nonce for Apple/Facebook → Firebase (hashed before sending to providers). */
export function createRawNonce(length = 32) {
    const chars =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const bytes = new Uint8Array(length);
    if (typeof crypto !== "undefined" && crypto.getRandomValues) {
        crypto.getRandomValues(bytes);
    } else {
        for (let i = 0; i < length; i += 1) {
            bytes[i] = Math.floor(Math.random() * 256);
        }
    }
    let out = "";
    for (let i = 0; i < length; i += 1) {
        out += chars[bytes[i] % chars.length];
    }
    return out;
}

export async function sha256Hex(value) {
    const data = new TextEncoder().encode(String(value));
    if (
        typeof crypto === "undefined" ||
        !crypto.subtle ||
        typeof crypto.subtle.digest !== "function"
    ) {
        let hash = 0;
        const str = String(value);
        for (let i = 0; i < str.length; i += 1) {
            hash = (hash << 5) - hash + str.charCodeAt(i);
            hash |= 0;
        }
        return `fallback_${Math.abs(hash).toString(16)}`;
    }
    const digest = await crypto.subtle.digest("SHA-256", data);
    return Array.from(new Uint8Array(digest))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");
}

/** Decode a JWT payload without verifying the signature (diagnostics only). */
export function decodeJwtPayload(token) {
    try {
        const parts = String(token || "").split(".");
        if (parts.length < 2) {
            return null;
        }
        const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
        const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
        const json =
            typeof atob === "function"
                ? atob(padded)
                : Buffer.from(padded, "base64").toString("utf8");
        return JSON.parse(json);
    } catch (_) {
        return null;
    }
}

export function looksLikeJwt(token) {
    const parts = String(token || "").split(".");
    return parts.length === 3 && parts.every((part) => part.length > 0);
}
